// Ad-click + campaign attribution capture (ads conversion tracking — Phase 1).
//
// Click IDs + UTM params only exist on the LANDING url, but a booking/enquiry
// is created many steps later — so we capture once on first load and persist
// for the whole session. When a conversion record exists we hand the captured
// attribution to record_campaign_attribution so server-side conversion
// reporting (Google Ads / Meta CAPI / GA4) can attribute the real booking or
// enquiry to its originating campaign. Best-effort: never block a booking.

import { supabase } from "../config";

const KEY = "vt-attribution";

// Tenant scope for the storage key. The shared hosted origin
// (book.vertu.co.za) serves EVERY venue, and a bare key there let one
// tenant's session read the campaign blob captured for another — the same
// class of leak the guest token already fixed (vt-guest-token:<tenantId>).
// Capture still happens pre-scope (the landing params must be grabbed before
// any navigation drops them), so scoping MOVES the staged blob under the
// tenant's own key the moment the widget knows who it is.
let scope = "";
let cachedSearch: string | null = null;
function keyName(): string {
  return scope ? `${KEY}:${scope}` : KEY;
}
export function scopeAttribution(tenantSlug: string): void {
  if (!tenantSlug || scope === tenantSlug) return;
  scope = tenantSlug;
  cached = null;
  try {
    const bare = sessionStorage.getItem(KEY);
    if (bare) {
      // Last-non-direct across the migration too: the bare blob is the FRESH
      // capture from this page load. If it carries a campaign signal it
      // outranks whatever the scoped store holds (which may be an older
      // campaign, or a signal-less organic capture that would otherwise bury
      // this click). Only a signal-less bare blob defers to existing data.
      let bareHasSignal = false;
      let parsedBare: Attribution | null = null;
      try {
        const parsed = JSON.parse(bare) as Attribution;
        parsedBare = parsed;
        bareHasSignal = hasCampaignSignal(parsed);
      } catch {
        /* unparseable → treat as signal-less */
      }
      if (bareHasSignal || !sessionStorage.getItem(keyName())) {
        sessionStorage.setItem(keyName(), bare);
      }
      // Capture happens before tenant scope is known. Mirror it to durable
      // storage now that the shared booking origin can isolate the tenant.
      if (bareHasSignal && parsedBare) persistDurable(parsedBare);
      // Always clear the bare blob: leaving it is the cross-tenant read.
      sessionStorage.removeItem(KEY);
    }
  } catch {
    /* storage unavailable — memory cache still applies */
  }
}

export interface Attribution {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
  ttclid?: string;
  /** Click-ledger receipt from tag.js on the tenant's own site — expanded
   *  server-side into the original click ids (20260807000070). */
  vt_click?: string;
  /** ValueTrack campaign/adgroup ids from the tenant's tracking template. */
  vt_cid?: string;
  vt_agid?: string;
  ga_client_id?: string;
  fbclid?: string;
  fbp?: string; // Meta _fbp cookie (set by the pixel, if present)
  fbc?: string; // Meta _fbc cookie
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_url?: string;
  referrer?: string;
  captured_at?: string;
}

// One definition for every signal that makes a visit campaign-led. Keep the
// click-ledger receipt and ValueTrack ids here too: both can be the only link
// between a booking and its campaign even when no raw platform click id made
// it across the tenant-site → hosted-widget boundary.
const CAMPAIGN_SIGNAL_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "vt_click",
  "vt_cid",
  "vt_agid",
] as const satisfies readonly (keyof Attribution)[];

function hasCampaignSignal(a: Attribution): boolean {
  return Boolean(
    CAMPAIGN_SIGNAL_KEYS.some((key) => a[key]) ||
    a.utm_source ||
    a.utm_medium ||
    a.utm_campaign ||
    a.utm_term ||
    a.utm_content,
  );
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"),
  );
  const v = m?.[1];
  return v != null ? decodeURIComponent(v) : undefined;
}

let cached: Attribution | null = null;

// Meta commonly opens the first visit inside Instagram/Facebook and the guest
// returns in another browser session days later. Session storage cannot bridge
// that ordinary path, so retain last-non-direct campaign identity for the same
// 90-day window as the tenant-site click receipt. The key is tenant-scoped.
const DURABLE_DAYS = 90;

function persistDurable(a: Attribution): void {
  if (!scope || !hasCampaignSignal(a)) return;
  try {
    localStorage.setItem(keyName(), JSON.stringify(a));
  } catch {
    /* private mode / blocked storage */
  }
}

function readDurable(): Attribution | null {
  if (!scope) return null;
  try {
    const raw = localStorage.getItem(keyName());
    if (!raw) return null;
    const a = JSON.parse(raw) as Attribution;
    const age = Date.now() - Date.parse(a.captured_at ?? "");
    if (!Number.isFinite(age) || age > DURABLE_DAYS * 86_400_000) {
      localStorage.removeItem(keyName());
      return null;
    }
    return a;
  } catch {
    return null;
  }
}

/**
 * Capture (once) and return the visitor's attribution. Reuses a prior capture
 * from this session — later panels won't have the click params in their URL.
 */
/** Campaign signal present in the CURRENT url — the last-non-direct test. */
function urlHasCampaignSignal(params: URLSearchParams): boolean {
  const current: Attribution = {};
  for (const key of CAMPAIGN_SIGNAL_KEYS) {
    const value = params.get(key);
    if (value) (current as Record<string, string>)[key] = value;
  }
  // Any UTM parameter is a campaign signal. A sentinel in the typed UTM slot
  // lets the shared predicate retain that broad URL behaviour.
  for (const [key, value] of params) {
    if (key.startsWith("utm_") && value) {
      current.utm_source = value;
      break;
    }
  }
  return hasCampaignSignal(current);
}

export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  // Memoised against the CURRENT query string, not forever: the observer
  // re-scan and the first conversion call used to latch the cache, so
  // campaign params arriving via same-document navigation (pushState) were
  // never recaptured for the life of the page.
  if (cached && cachedSearch === window.location.search) return cached;

  const params = new URLSearchParams(window.location.search);
  const freshSignal = urlHasCampaignSignal(params);

  // LAST-non-direct wins, not first-touch-forever. The old early return on
  // any stored value meant a guest who browsed organically and later clicked
  // a retargeting ad in the same tab had the ad's gclid DISCARDED — the
  // conversion credited to nothing. A fresh campaign signal in the URL always
  // recaptures; a signal-less visit keeps what was captured before.
  if (!freshSignal) {
    let stored: Attribution | null = null;
    try {
      const raw = sessionStorage.getItem(keyName());
      if (raw) stored = JSON.parse(raw) as Attribution;
    } catch {
      /* sessionStorage may be unavailable in some embeds — fall through */
    }
    // Signal-bearing session data is freshest. Identity-only session noise
    // must not shadow a durable campaign from the guest's earlier ad visit.
    if (stored && hasCampaignSignal(stored)) {
      cached = stored;
      cachedSearch = window.location.search;
      return cached;
    }
    const durable = readDurable();
    if (durable) {
      // Preserve the durable campaign and its original captured_at, but use
      // browser identities refreshed during this session when available.
      const merged: Attribution = {
        ...durable,
        ...(stored?.fbp ? { fbp: stored.fbp } : {}),
        ...(stored?.fbc ? { fbc: stored.fbc } : {}),
        ...(stored?.ga_client_id ? { ga_client_id: stored.ga_client_id } : {}),
      };
      cached = merged;
      cachedSearch = window.location.search;
      try {
        sessionStorage.setItem(keyName(), JSON.stringify(merged));
      } catch {
        /* ignore */
      }
      return cached;
    }
    if (stored) {
      cached = stored;
      cachedSearch = window.location.search;
      return cached;
    }
  }

  const pick = (k: string) => params.get(k) || undefined;
  const a: Attribution = {};

  for (const k of ["gclid", "gbraid", "wbraid", "msclkid", "ttclid", "vt_cid", "vt_agid"] as const) {
    const v = pick(k);
    if (v) (a as Record<string, string>)[k] = v;
  }
  // The tag.js receipt: on the hosted booking page it arrives as a URL param;
  // on an EMBED (widget on another page of the tenant's own site) it arrives
  // as the first-party cookie tag.js set on the landing page — same origin.
  // A 90-day cookie is last-non-direct evidence only. On a fresh campaign URL
  // it may belong to the guest's previous ad, so only an explicit URL receipt
  // can win; otherwise file this landing and obtain a new receipt.
  const vtClick =
    pick("vt_click") ?? (!freshSignal ? readCookie("vt_click") : undefined);
  if (vtClick) a.vt_click = vtClick;
  // GA4's client id ("GA1.2.<cid>.<ts>" → "<cid>.<ts>") — what lets a
  // server-side purchase stitch to the browser session instead of minting a
  // phantom new user per conversion.
  const ga = readCookie("_ga");
  if (ga) {
    const parts = ga.split(".");
    if (parts.length >= 4) a.ga_client_id = parts.slice(2).join(".");
  }
  const fbclid = pick("fbclid");
  if (fbclid) a.fbclid = fbclid;
  for (const u of ["source", "medium", "campaign", "term", "content"] as const) {
    const v = pick("utm_" + u);
    if (v) (a as Record<string, string>)["utm_" + u] = v;
  }
  const fbp = readCookie("_fbp");
  if (fbp) a.fbp = fbp;
  const fbc = readCookie("_fbc");
  if (fbc) a.fbc = fbc;
  a.landing_url = window.location.href.slice(0, 1000);
  if (document.referrer) a.referrer = document.referrer.slice(0, 500);
  a.captured_at = new Date().toISOString();

  cached = a;
  cachedSearch = window.location.search;
  try {
    sessionStorage.setItem(keyName(), JSON.stringify(a));
  } catch {
    /* ignore */
  }
  persistDurable(a);
  return a;
}

/** Any real campaign signal — what the ROI report groups a booking under. */
export function hasAttribution(a: Attribution): boolean {
  return hasCampaignSignal(a);
}

/**
 * Worth sending to the server: a campaign signal OR an id an ad platform can
 * MATCH on.
 *
 * The old test asked only "did this visit come from an ad?", and _fbp was not
 * even in it. But _fbp is the browser id Meta matches a Purchase against, set
 * by the tenant's own pixel on every visitor — ad-driven or not. Dropping it
 * meant every server-side Purchase left with a hashed email and nothing else:
 * Meta accepts such an event, files it under no campaign, and the tenant
 * concludes Vertu sends them nothing. Measured 2026-08-17, all 9 Meta
 * conversions in the outbox had campaign_metadata = {}.
 *
 * Recording these does not invent campaign credit — campaign_performance still
 * joins on vt_cid / utm_campaign, so an fbp-only row stays in the unattributed
 * bucket exactly as a missing row did. It only gives the CAPI something to
 * match on.
 */
function worthRecording(a: Attribution): boolean {
  return hasAttribution(a) || Boolean(a.fbp || a.fbc || a.ga_client_id);
}

/**
 * Re-read the cookie-derived ids and fold them into the capture.
 *
 * getAttribution() runs at mount (main.tsx), which is BEFORE initTracking
 * injects the Meta pixel — so on a first visit `_fbp` does not exist yet, and
 * the snapshot taken then has no browser id at all. The cache is keyed on the
 * query string, so nothing re-reads the cookie later either and the whole
 * visit stays blind. Only these three are volatile; campaign params are in the
 * URL from the first paint or never.
 */
function withFreshIdentity(a: Attribution): Attribution {
  const out: Attribution = { ...a };
  const fbp = readCookie("_fbp");
  if (fbp) out.fbp = fbp;
  const fbc = readCookie("_fbc");
  if (fbc) out.fbc = fbc;
  if (!out.ga_client_id) {
    const ga = readCookie("_ga");
    if (ga) {
      const parts = ga.split(".");
      if (parts.length >= 4) out.ga_client_id = parts.slice(2).join(".");
    }
  }
  return out;
}

// A landing the click ledger would otherwise never see.
//
// tag.js files campaign_clicks, and tag.js lives on the TENANT's website. That
// covers exactly one topology: an ad that lands on the tenant's own page, where
// the guest then clicks through to the widget. It covers neither of the other
// two, and platform-wide those are most of them — over 30 days the entire
// ledger held 644 rows from a single host.
//
//   * ads pointed straight at the hosted booking page (book.vertu.co.za), which
//     is what Laborie's Meta campaign actually does — no tag.js there, so 709
//     link clicks and 149 landing-page views left no trace at all;
//   * a widget EMBEDDED on a tenant page that does not carry the tag.
//
// In both, the widget is the only thing that ever sees the landing URL, so the
// widget is what has to file. Attribution on the BOOKING was already fine in
// all three — this is about the landings that never convert, which is precisely
// the population you need to tell "the ad sends nobody" apart from "the ad
// sends people and the page loses them".
//
// The dedup rule is the receipt, not a guess: holding a vt_click means tag.js
// already filed this landing, so we stay out of its way. No receipt plus a live
// campaign signal in the URL means nobody filed it.
const FILED_KEY = "vt-click-filed";

export function fileClickIfUnledgered(tenantSlug: string): void {
  if (typeof window === "undefined" || !tenantSlug) return;
  try {
    const params = new URLSearchParams(window.location.search);
    // Organic visit — filing one would be noise, not measurement.
    if (!urlHasCampaignSignal(params)) return;

    const a = withFreshIdentity(getAttribution());
    // tag.js already ledgered this landing and its receipt is riding the URL or
    // the first-party cookie. Filing again would double-count the click.
    if (a.vt_click) return;

    // Once per campaign signal per session. The widget re-mounts on SPA
    // navigation and the observer re-scans, and neither is a new click.
    const mark = `${FILED_KEY}:${tenantSlug}:${window.location.search}`;
    try {
      if (sessionStorage.getItem(mark)) return;
      sessionStorage.setItem(mark, "1");
    } catch {
      /* Private mode / blocked storage: fall through and file. A duplicate row
         is a smaller error than a landing we never saw. */
    }

    const metadata: Record<string, string> = {};
    for (const [k, v] of Object.entries(a)) {
      // captured_at is ours; vt_click cannot be set here (guarded above). Every
      // other key the RPC recognises it reads by name, and ignores the rest.
      if (k === "captured_at" || typeof v !== "string" || !v) continue;
      metadata[k] = v;
    }
    if (Object.keys(metadata).length === 0) return;

    // Non-blocking, but keep the receipt. It is the durable server-side bridge
    // when this landing converts in a later browser session.
    void supabase
      .rpc("record_campaign_click", {
        p_tenant_slug: tenantSlug,
        p_metadata: metadata,
      })
      .then(
        ({ data }) => {
          const id = typeof data === "string" ? data : null;
          if (!id || id.length < 32) return;
          try {
            const enriched: Attribution = { ...getAttribution(), vt_click: id };
            cached = enriched;
            sessionStorage.setItem(keyName(), JSON.stringify(enriched));
            persistDurable(enriched);
          } catch {
            /* measurement never breaks the booking flow */
          }
        },
        () => undefined,
      );
  } catch {
    /* measurement is never allowed to break the booking flow */
  }
}

/**
 * Record the captured attribution against a just-created enquiry (by id) or the
 * current cart's bookings (by cart_token). Best-effort and write-once on the
 * server — failures never surface to the customer.
 */
export async function recordAttribution(opts: {
  enquiryId?: string;
  cartToken?: string;
}): Promise<void> {
  try {
    const a = withFreshIdentity(getAttribution());
    if (!worthRecording(a)) return;
    // Keep the enriched blob so a later record (a second cart, the enquiry after
    // a booking) starts from the ids we have now rather than the blind snapshot.
    cached = a;
    try {
      sessionStorage.setItem(keyName(), JSON.stringify(a));
    } catch {
      /* ignore */
    }
    persistDurable(a);

    const writes = [];
    // Cart-level storage exists before every downstream record type does. In
    // particular, ChiDesk-only carts have neither a booking nor a payment row
    // until checkout, so the legacy recorder otherwise has nowhere to write.
    if (scope && opts.cartToken) {
      writes.push(
        supabase.rpc("record_cart_attribution", {
          p_tenant_slug: scope,
          p_cart_token: opts.cartToken,
          p_metadata: a,
        }),
      );
    }
    writes.push(
      supabase.rpc("record_campaign_attribution", {
        p_metadata: a,
        p_enquiry_id: opts.enquiryId ?? null,
        p_cart_token: opts.cartToken ?? null,
      }),
    );
    // Attribution is important, but checkout cannot hang behind a blocked
    // analytics request. Give the writes a short completion window; they keep
    // settling in the background if the browser remains on the page.
    await Promise.race([
      Promise.allSettled(writes),
      new Promise<void>((resolve) => window.setTimeout(resolve, 1500)),
    ]);
  } catch {
    /* attribution is non-critical — never block the booking/enquiry */
  }
}
